import * as React from 'react'
// libs
import { useMutation, useQuery } from '@apollo/client/react'
// components
import { LoaderWithOverlay } from 'components/UI/Loader'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
// graphql
import { SECURITY_PROFILES_QUERY } from 'graphql/securityProfiles/queries'
import {
  SECURITY_PROFILE_CREATE_MUTATION,
  SECURITY_PROFILE_DELETE_MUTATION,
  SECURITY_PROFILE_UPDATE_MUTATION
} from 'graphql/securityProfiles/mutations'
// hooks
import { useCurrentUser } from 'hooks/index'
// types
import { TRefetch } from 'types/refetch'
import { ISecurityProfile, ISecurityProfileResponse, ISecurityProfilesQueryArgs } from 'types/graphql'
// utils
import get from 'lodash/get'
import omit from 'lodash/omit'
import { handleError } from 'utils/errors/handlers'
import { getValueFromOption } from 'utils/form'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  data: ISecurityProfile[]
  refetch: TRefetch<ISecurityProfileResponse, ISecurityProfilesQueryArgs>
  isLoading: boolean
  totalCount: number
  isRefetching: boolean
  createSecurityProfile: (data: any, hideModal: () => void) => Promise<void>
  deleteSecurityProfile: (id: string, hideModal: () => void) => Promise<void>
  updateSecurityProfile: (id: string, data: any, hideModal: () => void) => Promise<void>
}

export const SecurityProfilesContext = React.createContext<IContext>({} as any)

const SecurityProfilesAPI: React.FC<Props> = ({ children }) => {
  const user = useCurrentUser()

  const [createSecurityProfileMutation] = useMutation(SECURITY_PROFILE_CREATE_MUTATION)
  const [deleteSecurityProfileMutation] = useMutation(SECURITY_PROFILE_DELETE_MUTATION)
  const [updateSecurityProfileMutation] = useMutation(SECURITY_PROFILE_UPDATE_MUTATION)

  const { filter, page, perPage, search } = React.useContext(TableManagementContext)

  const { data, networkStatus, refetch } = useQuery(SECURITY_PROFILES_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        companyId: filter?.companyId,
        name: search
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const profiles: ISecurityProfile[] = get(data, 'securityProfiles.data', [])
  const isLoading = networkStatus === 1
  const companyId = filter.companyId || user.companyId
  const totalCount: number = get(data, 'securityProfiles.pagination.totalCount')

  const getPermissionsFromData = (data) => {
    const permissions = { ...data }

    Object.keys(permissions).forEach((key) => {
      const value = permissions[key]

      if (typeof value === 'object') {
        return (permissions[key] = getValueFromOption(value))
      }
    })

    return omit(permissions, ['name', 'active'])
  }

  const createSecurityProfile = async (data: any, hideModal: () => void) => {
    try {
      await createSecurityProfileMutation({
        variables: {
          companyId,
          input: {
            active: data.active,
            name: data.name,
            permissions: getPermissionsFromData(data)
          }
        }
      })

      await refetch()
      hideModal()
    } catch (error) {
      handleError(error, 'SecurityProfilesAPI.createSecurityProfile')
    }
  }

  const deleteSecurityProfile = async (id: string, hideModal: () => void) => {
    try {
      await deleteSecurityProfileMutation({
        variables: {
          id,
          companyId
        }
      })

      await refetch()
      hideModal()
    } catch (error) {
      handleError(error, 'SecurityProfilesAPI.updateSecurityProfile')
    }
  }

  const updateSecurityProfile = async (id: string, data: any, hideModal: () => void) => {
    try {
      await updateSecurityProfileMutation({
        variables: {
          id,
          companyId,
          input: {
            active: data.active,
            name: data.name,
            permissions: getPermissionsFromData(data)
          }
        }
      })

      await refetch()
      hideModal()
    } catch (error) {
      handleError(error, 'SecurityProfilesAPI.updateSecurityProfile')
    }
  }

  const value = {
    refetch,
    totalCount,
    updateSecurityProfile,
    createSecurityProfile,
    deleteSecurityProfile,
    data: profiles,
    isLoading: networkStatus === 1,
    isRefetching: networkStatus === 2
  }

  return (
    <>
      <LoaderWithOverlay isLoading={isLoading} height={'calc(100% - 49px)'} />
      {!isLoading && <SecurityProfilesContext.Provider value={value}>{children}</SecurityProfilesContext.Provider>}
    </>
  )
}

export default SecurityProfilesAPI
